import { cn } from '@/lib/utils';
import type { XoloState } from '@/lib/delight/types';

export interface XoloDogAnimationProps {
  state: XoloState;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'w-8 h-8',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
} as const;

const animationClasses = {
  idle: '',
  wag: 'animate-xolo-wag',
  celebrate: 'animate-xolo-celebrate',
} as const;

/**
 * XoloDogAnimation - Renders the Xolo mascot with CSS animations
 *
 * Current: Uses placeholder SVG with CSS animations
 * Future: Will load actual Xolo mascot asset
 *
 * Respects prefers-reduced-motion via CSS media query
 *
 * Note: Uses inline fill/stroke colors instead of Tailwind classes
 * because Tailwind's fill-* utilities don't reliably compile for SVG elements
 */
export function XoloDogAnimation({ state, size = 'md', className }: XoloDogAnimationProps) {
  return (
    <div
      className={cn(
        'inline-flex items-center justify-center motion-reduce:animate-none',
        sizeClasses[size],
        animationClasses[state],
        className,
      )}
      role="img"
      aria-label="Xolo mascot"
    >
      {/* Placeholder SVG - replace with actual Xolo asset later */}
      <svg
        viewBox="0 0 64 64"
        className="w-full h-full"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Background circle - cream color for visibility against any toast */}
        <circle cx="32" cy="32" r="30" fill="#fffbeb" />
        {/* Body - friendly dog shape */}
        <ellipse cx="32" cy="36" rx="20" ry="16" fill="#b45309" />
        {/* Head */}
        <circle cx="32" cy="22" r="14" fill="#b45309" />
        {/* Left ear */}
        <ellipse cx="22" cy="12" rx="4" ry="8" fill="#92400e" transform="rotate(-15 22 12)" />
        {/* Right ear */}
        <ellipse cx="42" cy="12" rx="4" ry="8" fill="#92400e" transform="rotate(15 42 12)" />
        {/* Left eye */}
        <circle cx="26" cy="20" r="3" fill="#1f2937" />
        <circle cx="27" cy="19" r="1" fill="#ffffff" />
        {/* Right eye */}
        <circle cx="38" cy="20" r="3" fill="#1f2937" />
        <circle cx="39" cy="19" r="1" fill="#ffffff" />
        {/* Nose */}
        <ellipse cx="32" cy="26" rx="3" ry="2" fill="#1f2937" />
        {/* Smile */}
        <path
          d="M26 30 Q32 34 38 30"
          stroke="#1f2937"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
        {/* Tail - animated via CSS */}
        <path
          d="M50 40 Q58 36 54 28"
          stroke="#92400e"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
          className={cn(
            'origin-[50px_40px]',
            state === 'wag' && 'animate-xolo-tail-wag',
            state === 'celebrate' && 'animate-xolo-tail-wag',
          )}
        />
        {/* Front legs */}
        <rect x="24" y="46" width="4" height="10" rx="2" fill="#b45309" />
        <rect x="36" y="46" width="4" height="10" rx="2" fill="#b45309" />
      </svg>
    </div>
  );
}

export type { XoloState };
